/** File names, in the order they should be written. */
export const MULTIFILE_NAMES = [
    'core.json',
    'schedules.json',
    'notes.json',
    'instances.json',
    'journal.json',
    'reviews.json',
    'fitness.json',
    'tombstones.json',
];
export const SYNC_METADATA_FILE = 'sync-metadata.json';
/** Split ExportData into the per-file groups. */
export function exportToMultiFile(data) {
    const now = new Date().toISOString();
    return {
        core: {
            version: data.version,
            exportedAt: now,
            tasks: data.tasks,
            projects: data.projects,
            goals: data.goals,
            lists: data.lists,
            preferences: data.preferences,
            stats: data.stats,
        },
        schedules: {
            version: data.version,
            exportedAt: now,
            schedules: data.schedules,
        },
        notes: {
            version: data.version,
            exportedAt: now,
            notes: data.notes,
        },
        instances: {
            version: data.version,
            exportedAt: now,
            listInstances: data.listInstances,
            habitInstances: data.habitInstances,
        },
        reviews: {
            version: data.version,
            exportedAt: now,
            reviews: data.reviews || [],
        },
        fitness: {
            version: data.version,
            exportedAt: now,
            workouts: data.fitnessWorkouts || [],
            bodyweights: data.fitnessBodyweights || [],
            settings: data.fitnessSettings || [],
        },
        journal: {
            version: data.version,
            exportedAt: now,
            journalEntries: data.journalEntries || [],
            moodCheckIns: data.moodCheckIns || [],
        },
        tombstones: data.tombstones || { tombstones: [] },
    };
}
/** Reassemble ExportData from the per-file groups. */
export function multiFileToExport(multi) {
    return {
        version: multi.core.version,
        exportedAt: multi.core.exportedAt,
        tasks: multi.core.tasks,
        projects: multi.core.projects,
        goals: multi.core.goals,
        lists: multi.core.lists,
        preferences: multi.core.preferences,
        stats: multi.core.stats,
        schedules: multi.schedules.schedules,
        notes: multi.notes.notes,
        listInstances: multi.instances.listInstances,
        habitInstances: multi.instances.habitInstances,
        journalEntries: multi.journal?.journalEntries || [],
        moodCheckIns: multi.journal?.moodCheckIns || [],
        // Optional chaining and a default, not `multi.reviews.reviews`: a repo
        // written before this file existed has no reviews.json at all, and
        // treating that as a crash rather than "no reviews yet" would break sync
        // outright for every existing user.
        reviews: multi.reviews?.reviews || [],
        // Same defaulting as reviews: nothing written before 2.5 has fitness.json.
        fitnessWorkouts: multi.fitness?.workouts || [],
        fitnessBodyweights: multi.fitness?.bodyweights || [],
        fitnessSettings: multi.fitness?.settings || [],
        tombstones: multi.tombstones,
    };
}
/**
 * Does this file's SUBSTANCE match what is already stored?
 *
 * `exportToMultiFile` stamps `exportedAt: now` into all seven files on every
 * save, so every file differs from its stored copy every time even when
 * nothing in it changed. That made "write only what moved" impossible to see,
 * and the cost fell on GitHub, where each file is a separate commit and
 * commits to one branch must go up one at a time: adding a single task wrote
 * seven sequential commits, six of them differing only in a timestamp.
 *
 * That is the whole latency budget of a write. It is why writes through the
 * assistant intermittently timed out while reads did not, and a write that
 * timed out had usually already been stored — so the retry created a SECOND
 * copy of the task.
 *
 * Comparing with the timestamp removed lets the unchanged six be skipped.
 * Written to be wrong only in the safe direction: any parse failure, missing
 * file, or key-order difference returns false and the file is written. A
 * needless write costs a commit; a skipped necessary write loses data.
 */
export function contentUnchanged(previous, next) {
    if (!previous)
        return false;
    try {
        const before = JSON.parse(previous);
        const after = JSON.parse(next);
        delete before.exportedAt;
        delete after.exportedAt;
        return JSON.stringify(before) === JSON.stringify(after);
    }
    catch {
        return false;
    }
}
/**
 * Assemble MultiFileData from raw JSON strings (missing files get empty
 * defaults, so a partially-written remote never crashes a pull).
 * `contents.core` must be present — callers should treat a missing core as
 * "no remote data".
 */
export function parseMultiFile(contents) {
    const parse = (raw, fallback) => raw ? JSON.parse(raw) : fallback;
    return {
        core: JSON.parse(contents['core.json']),
        schedules: parse(contents['schedules.json'], { version: '1.0.0', exportedAt: '', schedules: [] }),
        notes: parse(contents['notes.json'], { version: '1.0.0', exportedAt: '', notes: [] }),
        instances: parse(contents['instances.json'], {
            version: '1.0.0', exportedAt: '', listInstances: [], habitInstances: [],
        }),
        reviews: parse(contents['reviews.json'], {
            version: '1.0.0', exportedAt: '', reviews: [],
        }),
        // A missing fitness.json reads as empty; a CORRUPT one throws (JSON.parse),
        // deliberately — unreadable is not empty (CLAUDE.md §3.6).
        fitness: parse(contents['fitness.json'], {
            version: '1.0.0', exportedAt: '', workouts: [], bodyweights: [], settings: [],
        }),
        journal: parse(contents['journal.json'], {
            version: '1.0.0', exportedAt: '', journalEntries: [], moodCheckIns: [],
        }),
        tombstones: parse(contents['tombstones.json'], { tombstones: [] }),
    };
}
/** Serialize each group to the string that should be stored in its file. */
export function multiFileToContents(multi) {
    return {
        'core.json': JSON.stringify(multi.core, null, 2),
        'schedules.json': JSON.stringify(multi.schedules, null, 2),
        'notes.json': JSON.stringify(multi.notes, null, 2),
        'instances.json': JSON.stringify(multi.instances, null, 2),
        'journal.json': JSON.stringify(multi.journal, null, 2),
        'reviews.json': JSON.stringify(multi.reviews, null, 2),
        'fitness.json': JSON.stringify(multi.fitness, null, 2),
        'tombstones.json': JSON.stringify(multi.tombstones, null, 2),
    };
}
//# sourceMappingURL=multifile.js.map